/**
 * Bausteine per Klick einsetzen — nicht nur per Ziehen.
 *
 * Ziehen ist im Editor die einzige Art gewesen, einen Baustein zu setzen. Das
 * ist der Grund, warum sich in eine zweite Spalte nichts einfügen ließ: eine
 * schmale Spalte ist ein winziges Ziel, und wer knapp danebentrifft, bekommt
 * seinen Baustein irgendwo anders — meist als neuer Abschnitt ganz unten.
 * Das Werkzeug wirkt dann kaputt, obwohl die Vorlage die Struktur ohne
 * Weiteres zulässt.
 *
 * Ein Klick ist eindeutig. Er setzt den Baustein an die Stelle, die sich aus
 * der Auswahl ergibt:
 *
 *   Spalte ausgewählt      → hinein, ans Ende
 *   Baustein ausgewählt    → direkt darunter, in dieselbe Spalte
 *   nichts ausgewählt      → ans Ende der Nachricht
 *
 * Ganze Abschnitte (Kopf, Zwei Spalten, Fußzeile) können nicht in eine Spalte;
 * sie landen immer auf der Ebene der Abschnitte. Das ist keine Einschränkung
 * des Werkzeugs, sondern von MJML - ein Abschnitt IN einer Spalte ergäbe kein
 * gültiges Layout.
 *
 * Ziehen bleibt unverändert möglich.
 */

/**
 * Ist der Inhalt dieses Bausteins ein ganzer Abschnitt?
 *
 * @param {string} inhalt
 * @returns {boolean}
 */
export function istAbschnitt(inhalt) {
  return /^\s*<\s*(mj-section|mj-wrapper)\b/i.test(String(inhalt ?? ''));
}

/**
 * Bestimmt, wo ein Baustein landet.
 *
 * @param {any} editor
 * @param {string} inhalt
 * @returns {{ ziel: any, stelle?: number, beschreibung: string } | null}
 */
export function einfuegeZiel(editor, inhalt) {
  const wrapper = editor.getWrapper();
  if (!wrapper) return null;

  const koerper = wrapper.find('[data-gjs-type="mj-body"]')[0] ?? wrapper;
  const gewaehlt = editor.getSelected();

  if (istAbschnitt(inhalt)) {
    // Hinter den Abschnitt, in dem gerade gearbeitet wird - sonst ans Ende.
    const abschnitt = vorfahrVomTyp(gewaehlt, 'mj-section');
    if (abschnitt && abschnitt.parent()) {
      const eltern = abschnitt.parent();
      return {
        ziel: eltern,
        stelle: eltern.components().indexOf(abschnitt) + 1,
        beschreibung: 'unter dem aktuellen Abschnitt',
      };
    }
    return { ziel: koerper, beschreibung: 'am Ende der Nachricht' };
  }

  // Ein einzelner Inhalt gehört in eine Spalte.
  if (gewaehlt) {
    if (gewaehlt.get('type') === 'mj-column') {
      return { ziel: gewaehlt, beschreibung: 'in die ausgewählte Spalte' };
    }
    const spalte = vorfahrVomTyp(gewaehlt, 'mj-column');
    if (spalte) {
      return {
        ziel: spalte,
        stelle: spalte.components().indexOf(gewaehlt) + 1,
        beschreibung: 'direkt unter dem ausgewählten Baustein',
      };
    }
  }

  const spalten = wrapper.find('[data-gjs-type="mj-column"]');
  if (spalten.length) {
    return { ziel: spalten[spalten.length - 1], beschreibung: 'in die letzte Spalte' };
  }

  // Noch gar keine Spalte vorhanden: eine anlegen, damit der Klick trotzdem
  // etwas bewirkt. Ein Klick, der nichts tut, ist die schlechteste Antwort.
  const neu = koerper.append('<mj-section><mj-column></mj-column></mj-section>');
  const angelegt = Array.isArray(neu) ? neu[0] : neu;
  const spalte = angelegt?.find?.('[data-gjs-type="mj-column"]')?.[0];
  return spalte
    ? { ziel: spalte, beschreibung: 'in einen neuen Abschnitt' }
    : { ziel: koerper, beschreibung: 'am Ende der Nachricht' };
}

/**
 * Setzt einen Baustein per Klick ein.
 *
 * @param {any} editor
 * @param {any} baustein Block aus dem BlockManager.
 * @returns {{ beschreibung: string, komponente: any } | null}
 */
export function bausteinEinsetzen(editor, baustein) {
  const inhalt = baustein.get('content');
  const ziel = einfuegeZiel(editor, inhalt);
  if (!ziel) return null;

  const eingefuegt = ziel.ziel.append(
    inhalt,
    ziel.stelle === undefined ? undefined : { at: ziel.stelle },
  );
  const komponente = Array.isArray(eingefuegt) ? eingefuegt[0] : eingefuegt;

  // Auswählen und in den Blick holen: sonst sucht man, wo der Baustein
  // gelandet ist - besonders bei einer langen Vorlage.
  if (komponente) {
    editor.select(komponente);
    komponente.view?.el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }

  return { beschreibung: ziel.beschreibung, komponente };
}

/**
 * Sucht den nächsten Vorfahren eines Typs - die Komponente selbst zählt mit.
 *
 * @param {any} komponente
 * @param {string} typ
 * @returns {any|null}
 */
function vorfahrVomTyp(komponente, typ) {
  let aktuell = komponente;
  while (aktuell) {
    if (aktuell.get?.('type') === typ) return aktuell;
    aktuell = aktuell.parent?.();
  }
  return null;
}
